import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Spinner, Center } from "@chakra-ui/react";
import DocumentEditorView from "./DocumentEditorView";
import { useAuth } from "../AuthContext";
import { useAuthenticatedRequest } from "../hooks/useAuthenticatedRequest";

/** Detect an "empty" tiptap doc */
const isEmptyDoc = (raw) => {
  if (!raw) return true;
  if (typeof raw === "string") {
    const t = raw.trim();
    return t.length === 0 || t === "{}" || t === "null";
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return Object.keys(raw).length === 0;
  }
  return false;
};

/** Parse content that may already be an object or a JSON string. */
const parseTiptapContent = (content) => {
  if (!content) return null;
  if (typeof content === "object") return content;
  try {
    return JSON.parse(content);
  } catch {
    return null;
  }
};

export default function DocumentEditorStandalone() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { get, put } = useAuthenticatedRequest();
  const [searchParams] = useSearchParams();
  const documentId = searchParams.get("doc");

  // Extract user roles from platformUser
  const userRoles = user?.platformUser?.roles || [];

  const [docFromRepo, setDocFromRepo] = useState(null);
  const [repoCodified, setRepoCodified] = useState([]);
  const [docMeta, setDocMeta] = useState(null);
  const [docLoading, setDocLoading] = useState(false);

  // Load the document from DocumentsAPI
  useEffect(() => {
    if (!documentId) {
      console.warn("No document ID provided");
      return;
    }

    const ac = new AbortController();
    setDocLoading(true);
    (async () => {
      try {
        const url = `https://documents.poc.mrc.ai/api/documents/${documentId}`;
        console.log("[DocumentEditorStandalone] Fetching document:", documentId);

        const response = await get(url);
        const docJson = response.data;

        console.log("[DocumentEditorStandalone] Document loaded - Full response:", docJson);
        console.log("[DocumentEditorStandalone] Document loaded - Key fields:", {
          id: docJson?._id,
          name: docJson?.name,
          definitionId: docJson?.definitionId,
          hasName: !!docJson?.name,
          nameValue: JSON.stringify(docJson?.name),
        });

        // Expect { content: TipTapJson | string, ... }
        const parsed = parseTiptapContent(docJson?.content);
        if (parsed) {
          setDocFromRepo(parsed);
        } else {
          console.warn("Document content was empty or unparsable.");
          setDocFromRepo(null);
        }

        setRepoCodified(Array.isArray(docJson?.codifiedContents) ? docJson.codifiedContents : []);

        const metaToSet = {
          documentRepositoryId: docJson?._id,
          definitionId: docJson?.definitionId,
          status: docJson?.status,
          createdDate: docJson?.createdDate,
          codifyCollection: docJson?.codifyCollection,
          dataStandard: docJson?.dataStandard,
          dataType: docJson?.dataType,
          model: docJson?.model,
          name: docJson?.name,
          codifySpecifications: docJson?.codifySpecifications,
          productCode: docJson?.productCode,
          isTemplate: docJson?.isTemplate,
          organisationOwnerId: docJson?.organisationOwnerId,
        };

        console.log("[DocumentEditorStandalone] Setting docMeta:", metaToSet);
        setDocMeta(metaToSet);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error loading document from DocumentsAPI:", err);
          setDocFromRepo(null);
        }
      } finally {
        setDocLoading(false);
      }
    })();

    return () => ac.abort();
  }, [documentId]);

  // Decide effective doc
  const effectiveDoc = useMemo(() => {
    if (docFromRepo && !isEmptyDoc(docFromRepo)) return docFromRepo;
    // Return a default empty document structure
    return {
      type: "doc",
      content: [
        {
          type: "paragraph",
          content: [],
        },
      ],
    };
  }, [docFromRepo]);

  // Save hook back to Documents API
  const handleSave = async (payload) => {
    const repoId = docMeta?.documentRepositoryId;
    if (!repoId) {
      console.error("[DocumentEditorStandalone] No documentRepositoryId to save against:", { docMeta });
      return { ok: false, message: "No documentRepositoryId to save against." };
    }

    console.log("[DocumentEditorStandalone] handleSave called:", {
      repoId,
      payloadKeys: Object.keys(payload || {}),
      payloadName: payload?.name,
      definitionId: payload?.definitionId
    });

    try {
      const response = await put(`https://documents.poc.mrc.ai/api/documents/${repoId}`, payload);

      console.log("[DocumentEditorStandalone] Save response:", {
        status: response.status,
        statusText: response.statusText
      });

      const savedDoc = response.data;
      console.log("[DocumentEditorStandalone] Document saved successfully:", {
        repoId,
        savedDocId: savedDoc?._id,
        name: savedDoc?.name
      });

      return { ok: true, data: savedDoc };
    } catch (err) {
      console.error("[DocumentEditorStandalone] Save exception:", err);
      return { ok: false, message: err.response?.data?.message || err?.message || "Save failed" };
    }
  };

  // Close handler - navigate back to studio homepage
  const handleClose = () => {
    navigate("/studio");
  };

  // Loading gate
  if (docLoading || !docMeta) {
    return (
      <Center height="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  // Render editor
  return (
    <DocumentEditorView
      tiptapDocument={effectiveDoc}
      userRole="editor"
      userRoles={userRoles}
      contract={null}
      documentMeta={{
        documentRepositoryId: docMeta?.documentRepositoryId || null,
        definitionId: docMeta?.definitionId || null,
        status: docMeta?.status || null,
        createdDate: docMeta?.createdDate || null,
        codifyCollection: docMeta?.codifyCollection || null,
        dataStandard: docMeta?.dataStandard || null,
        dataType: docMeta?.dataType || null,
        model: docMeta?.model || null,
        name: docMeta?.name || null,
        codifySpecifications: docMeta?.codifySpecifications || [],
        productCode: docMeta?.productCode || null,
        isTemplate: docMeta?.isTemplate ?? false,
        organisationOwnerId: docMeta?.organisationOwnerId || null,
      }}
      initialCodifiedContents={repoCodified}
      onSave={handleSave}
      onClose={handleClose}
    />
  );
}
